import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ChevronLeft, ChevronRight, Info, Search, X } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { type FilterToken } from "./TableSearch"

/**
 * The one search bar, everywhere. Same dock, same ⓘ→✕ toggle (also Cmd/Ctrl+F,
 * handled by the screen), same chip input — so the user learns it once and it
 * feels identical on every tab. Chips join with OR; the live input joins with
 * AND while empty, OR once typed — no operator UI. Chips are plain text terms
 * only. Only the *target* of the query changes (find in document / filter
 * articles / filter substances): that is expressed by the `placeholder`, by what
 * the screen does with the shared tokens, and by the `extra` slot (the document
 * adds its ▲▼ match- and ◀▶ source-navigation there).
 *
 * Ported from the regulatory-cms studio (components/PowerBar.tsx).
 */
export function PowerBar({
  open,
  onToggle,
  tokens,
  activeTerm,
  search,
  onTokensChange,
  onSearchChange,
  placeholder,
  extra,
  focusNonce = 0,
}: {
  open: boolean
  onToggle: () => void
  tokens: FilterToken[]
  /** The document's current-match term — the text chip that owns it renders
   *  active (yellow). */
  activeTerm?: string | null
  search: string
  onTokensChange: (tokens: FilterToken[]) => void
  onSearchChange: (s: string) => void
  placeholder?: string
  extra?: ReactNode
  /** Bumped by a caller that wants the input focused although the bar is
   *  already open (the article browser's search icon): the open flip alone
   *  would not re-run the focus below. */
  focusNonce?: number
}) {
  const { translate } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus())
  }, [open, focusNonce])

  const commit = () => {
    const q = search.trim()
    if (!q) return
    onTokensChange([...tokens, { term: { kind: "text", q }, op: "or" }])
    onSearchChange("")
  }

  const chipLabel = (tok: FilterToken) => `“${tok.term.q}”`

  // Case-insensitive, whitespace-collapsed compare for the active-chip match.
  const norm = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase()
  // The active chip owns the document's current ▲▼ match: a text chip whose
  // keyword equals the match term. It renders yellow.
  const isActive = (tok: FilterToken) =>
    activeTerm != null && norm(tok.term.q) === norm(activeTerm)

  // Chips live above the input as one non-wrapping strip that scrolls
  // horizontally (hidden scrollbar); ‹/› nudge buttons appear while it
  // overflows (click = one step, hold = accelerating sweep to that end).
  const scrollRef = useRef<HTMLDivElement>(null)
  const [overflowing, setOverflowing] = useState(false)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => setOverflowing(el.scrollWidth > el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [open, tokens])

  const prevLen = useRef(tokens.length)
  useEffect(() => {
    // A new chip snaps the strip to the end so the latest stays visible.
    if (tokens.length > prevLen.current)
      scrollRef.current?.scrollTo({
        left: scrollRef.current.scrollWidth,
        behavior: "smooth",
      })
    prevLen.current = tokens.length
  }, [tokens.length])

  useEffect(() => {
    // Keep the chip echoing the document's current match in view.
    if (!activeTerm) return
    scrollRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" })
  }, [activeTerm])

  // ‹ › nudge buttons: a click steps the strip a granular STEP so no chip is
  // skipped past; holding sweeps toward that end along an exponential ease —
  // slow start, then sprinting — so ~2.5s of hold reaches the extreme of any
  // strip length. A press shorter than the hold delay is the click.
  const STEP = 110
  const HOLD_DELAY_MS = 300
  const SWEEP_MS = 2500
  const hold = useRef<{ raf: number; timer: number; engaged: boolean } | null>(
    null,
  )
  const nudge = (dir: 1 | -1) =>
    scrollRef.current?.scrollBy({ left: dir * STEP, behavior: "smooth" })
  const startHold = (dir: 1 | -1) => (e: React.PointerEvent) => {
    if (e.button !== 0) return
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    const state = { raf: 0, timer: 0, engaged: false }
    hold.current = state
    state.timer = window.setTimeout(() => {
      const el = scrollRef.current
      if (!el) return
      state.engaged = true
      el.style.scrollBehavior = "auto" // the frame loop must not be smoothed
      const from = el.scrollLeft
      const target = dir > 0 ? el.scrollWidth - el.clientWidth : 0
      const t0 = performance.now()
      const tick = (now: number) => {
        const p = Math.min(1, (now - t0) / SWEEP_MS)
        const eased = p === 0 ? 0 : Math.pow(2, 10 * (p - 1))
        el.scrollLeft = from + (target - from) * eased
        if (p < 1) state.raf = requestAnimationFrame(tick)
      }
      state.raf = requestAnimationFrame(tick)
    }, HOLD_DELAY_MS)
  }
  const endHold = (dir: 1 | -1, step: boolean) => () => {
    const state = hold.current
    hold.current = null
    if (!state) return
    clearTimeout(state.timer)
    cancelAnimationFrame(state.raf)
    if (scrollRef.current) scrollRef.current.style.scrollBehavior = ""
    if (step && !state.engaged) nudge(dir)
  }
  // Unmount mid-hold must not leave a frame loop running.
  useEffect(
    () => () => {
      if (hold.current) {
        clearTimeout(hold.current.timer)
        cancelAnimationFrame(hold.current.raf)
      }
    },
    [],
  )

  const renderChip = (tok: FilterToken, i: number) => {
    const active = isActive(tok)
    return (
      <span
        key={i}
        data-active={active ? "true" : undefined}
        // Neutral pill idle; turns yellow (pill-current) while it owns the
        // document's current ▲▼ match.
        className={`pill ${active ? "pill-current" : "pill-white"}`}
        style={{ gap: 5, flexShrink: 0, maxWidth: 180 }}
        title={translate("MC_DECL_DOC_CHIP_CONTAINS", { q: tok.term.q })}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {chipLabel(tok)}
        </span>
        <svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          style={{ flexShrink: 0, opacity: 0.7, cursor: "pointer" }}
          onClick={() => onTokensChange(tokens.filter((_, j) => j !== i))}
        >
          <title>{translate("MC_DECL_DOC_CHIP_REMOVE")}</title>
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </span>
    )
  }

  return (
    <div className="doc-bar-dock">
      {open && (
        <div className="doc-bar" role="search">
          {tokens.length > 0 && (
            <div className="doc-bar-chips">
              {overflowing && (
                <button
                  type="button"
                  className="doc-bar-btn"
                  aria-label={translate("MC_DECL_DOC_FILTERS_LEFT_ARIA")}
                  title={translate("MC_DECL_DOC_FILTERS_LEFT")}
                  onPointerDown={startHold(-1)}
                  onPointerUp={endHold(-1, true)}
                  onPointerCancel={endHold(-1, false)}
                  onClick={(e) => e.detail === 0 && nudge(-1)}
                >
                  <ChevronLeft size={13} />
                </button>
              )}
              <div ref={scrollRef} className="doc-bar-chips-scroll">
                {tokens.map(renderChip)}
              </div>
              {overflowing && (
                <button
                  type="button"
                  className="doc-bar-btn"
                  aria-label={translate("MC_DECL_DOC_FILTERS_RIGHT_ARIA")}
                  title={translate("MC_DECL_DOC_FILTERS_RIGHT")}
                  onPointerDown={startHold(1)}
                  onPointerUp={endHold(1, true)}
                  onPointerCancel={endHold(1, false)}
                  onClick={(e) => e.detail === 0 && nudge(1)}
                >
                  <ChevronRight size={13} />
                </button>
              )}
              {tokens.length >= 2 && (
                <>
                  <span className="doc-bar-divider" />
                  <button
                    type="button"
                    className="doc-bar-clear"
                    title={translate("MC_DECL_DOC_CLEAR_ALL")}
                    onClick={() => onTokensChange([])}
                  >
                    {translate("MC_DECL_DOC_CLEAR", { count: tokens.length })}
                  </button>
                </>
              )}
            </div>
          )}
          <div className="doc-bar-row">
            <Search size={14} className="doc-bar-search-icon" />
            <input
              ref={inputRef}
              className="doc-bar-input"
              value={search}
              placeholder={placeholder}
              spellCheck={false}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  commit()
                } else if (e.key === "Backspace" && search === "") {
                  onTokensChange(tokens.slice(0, -1))
                } else if (e.key === "Escape") {
                  e.preventDefault()
                  onToggle()
                }
              }}
            />
            {extra}
          </div>
        </div>
      )}
      <button
        className="doc-bar-toggle"
        onClick={onToggle}
        title={
          open
            ? translate("MC_DECL_DOC_SEARCH_CLOSE")
            : translate("MC_DECL_DOC_SEARCH_OPEN")
        }
        aria-label={
          open
            ? translate("MC_DECL_DOC_SEARCH_CLOSE_ARIA")
            : translate("MC_DECL_DOC_SEARCH_OPEN_ARIA")
        }
      >
        {open ? <X size={15} /> : <Info size={15} />}
      </button>
    </div>
  )
}
